import React, { useMemo, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";

const purple = "#2E2868";

const MedicationSubsection = ({
  subjectId,
  isCreate,
  form,
  medication,
  setMedication,
  isOpen,
  onToggle,
  saveSubject,
  loadSubject,
  getMedications,
}) => {
  const [showMedicationForm, setShowMedicationForm] = useState(false);
  const [editingMedicationIndex, setEditingMedicationIndex] = useState(null);

  const [medicationOptions, setMedicationOptions] = useState([]);
  const [isCustomSelected, setIsCustomSelected] = useState(false);

  const [newMedication, setNewMedication] = useState({
    id: null,
    medication_id: null, 
    medication_name: "",
    dosage_and_frequency: "",
    notes: "",
  });

  // --- Load medications  ---
  useEffect(() => {
    let mounted = true;
    (async () => {
      const res = await getMedications();
      if (!mounted) return;
      setMedicationOptions(res || []);
    })();
    return () => {
      mounted = false;
    };
  }, [getMedications]);

  const handleMedicationChange = (field, val) =>
    setNewMedication((prev) => ({ ...prev, [field]: val }));

  const resetForm = () => {
    setNewMedication({
      id: null,
      medication_id: null,
      medication_name: "",
      dosage_and_frequency: "",
      notes: "",
    });
    setEditingMedicationIndex(null);
    setShowMedicationForm(false);
    setIsCustomSelected(false);
  };

  // --- Helpers: safe id matching ---
  const findMedicationById = (rawId) => {
    if (rawId === "" || rawId == null) return null;
    if (rawId === "__CUSTOM__") return { id: null, name: "Custom Medication" };

    return (
      (medicationOptions || []).find((m) => String(m?.id) === String(rawId)) ||
      null
    );
  };

 
  const medicationSelectOptions = useMemo(() => {
    return (medicationOptions || []).map((m) => {
      const isCustom = m?.id == null;
      return {
        value: isCustom ? "__CUSTOM__" : String(m.id),
        label: m?.name ?? String(m?.id ?? ""),
        isCustom,
        raw: m,
      };
    });
  }, [medicationOptions]);

  const selectedMedicationValue = useMemo(() => {
    if (isCustomSelected) return "__CUSTOM__";
    return newMedication.medication_id == null
      ? ""
      : String(newMedication.medication_id);
  }, [isCustomSelected, newMedication.medication_id]);

  const syncFromFreshSubject = (fresh) => {
    if (!fresh?.subject_medications) return;

    setMedication(
      fresh.subject_medications.map((m) => ({
        id: m.id,
        medication_id: m.medication_id ?? null,
        medication_name:
          m.medication_name ||
          m.medication_id_name ||
          (m.medication && m.medication.name) ||
          "",
        dosage_and_frequency: m.dosage_and_frequency ?? "",
        notes: m.notes ?? "",
      })),
    );
  };

  const saveMedication = async () => {
    const hasMedication =
      newMedication.medication_id != null ||
      !!newMedication.medication_name?.trim();
    if (!hasMedication || !newMedication.dosage_and_frequency?.trim()) return;

    const payloadObject = {
      ...(newMedication.id && { id: newMedication.id }),
      medication_id: newMedication.medication_id ?? null,
      medication_name:
        newMedication.medication_id != null
          ? null
          : newMedication.medication_name.trim(),
      dosage_and_frequency: (newMedication.dosage_and_frequency || "").trim(),
      notes: (newMedication.notes || "").trim() || null,
    };

    const updatedList =
      editingMedicationIndex !== null
        ? medication.map((item, idx) =>
            idx === editingMedicationIndex
              ? { ...item, ...payloadObject }
              : item,
          )
        : [...medication, payloadObject];

    // Create mode -> client-side only
    if (isCreate && form?.setValue) {
      setMedication(updatedList);
      form.setValue({ key: "subject_medications", value: updatedList });
      resetForm();
      return;
    }

    // Edit mode -> backend
    await saveSubject({
      input: {
        subjects: { id: subjectId },
        subject_medications: [payloadObject],
      },
    });

    //  Force reload to bypass stale rootContext cache
    const fresh = await loadSubject({ force: true });
    syncFromFreshSubject(fresh);

    resetForm();
  };

  const deleteCurrentMedication = async () => {
    if (isCreate || editingMedicationIndex === null) return;

    const record = medication[editingMedicationIndex];
    if (!record?.id) return;

    await saveSubject({
      input: { subjects: { id: subjectId } },
      input_delete: [{ id: record.id, key: "subject_medications" }],
    });

    const fresh = await loadSubject({ force: true });
    syncFromFreshSubject(fresh);

    resetForm();
  };

  return (
    <div className="mb-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div
          className="d-flex align-items-center"
          onClick={onToggle}
          style={{ cursor: "pointer" }}
        >
          <i
            className={`bi ${isOpen ? "bi-chevron-down" : "bi-chevron-right"}`}
            style={{ color: purple, fontSize: "14px", marginRight: "6px" }}
          />
          <h6 className="fw-bold m-0" style={{ color: purple }}>
            Medication ({medication.length})
          </h6>
        </div>

        <span
          onClick={() => {
            setShowMedicationForm(true);
            setEditingMedicationIndex(null);
            setNewMedication({
              id: null,
              medication_id: null,
              medication_name: "",
              dosage_and_frequency: "",
              notes: "",
            });
            setIsCustomSelected(false);
          }}
          style={{
            fontSize: "13px",
            color: purple,
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          + Add
        </span>
      </div>

      {/* FORM */}
      {showMedicationForm && (
        <div
          className="p-3 mb-3 position-relative"
          style={{ background: "#f2f2f7", borderRadius: "12px" }}
        >
          <span
            onClick={() => setShowMedicationForm(false)}
            style={{
              position: "absolute",
              right: "12px",
              top: "10px",
              cursor: "pointer",
              color: purple,
              fontWeight: "bold",
              fontSize: "23px",
            }}
          >
            ×
          </span>

          {/* Medication */}
          <div className="mb-2">
            <label className="mb-1">Medication</label>
            <select
              value={selectedMedicationValue}
              onChange={(e) => {
                const val = e.target.value;

                if (val === "__CUSTOM__") {
                  setIsCustomSelected(true);
                  setNewMedication((prev) => ({
                    ...prev,
                    medication_id: null,
                    medication_name: "",
                  }));
                  return;
                }

                const selected = findMedicationById(val);
                setIsCustomSelected(false);
                setNewMedication((prev) => ({
                  ...prev,
                  medication_id: selected?.id ?? null,
                  medication_name: selected?.name ?? "",
                }));
              }}
              className="form-control rounded-pill border-0"
            >
              <option value="">Select medication</option>
              {medicationSelectOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Custom Medication Name */}
          {isCustomSelected && (
            <div className="mb-2">
              <label className="mb-1">Custom Medication Name</label>
              <input
                value={newMedication.medication_name}
                onChange={(e) =>
                  handleMedicationChange("medication_name", e.target.value)
                }
                className="form-control rounded-pill border-0"
                placeholder="Enter medication name"
              />
            </div>
          )}

          {/* Dosage */}
          <div className="mb-2">
            <label className="mb-1">Dosage & Frequency</label>
            <input
              value={newMedication.dosage_and_frequency}
              onChange={(e) =>
                handleMedicationChange("dosage_and_frequency", e.target.value)
              }
              className="form-control rounded-pill border-0"
            />
          </div>

          {/* Notes */}
          <div className="mb-3">
            <label className="mb-1">Notes</label>
            <textarea
              rows={2}
              value={newMedication.notes}
              onChange={(e) => handleMedicationChange("notes", e.target.value)}
              className="form-control rounded-3 border-0"
            />
          </div>

          <div className="d-flex justify-content-center gap-3">
            <button
              className="btn btn-sm"
              onClick={saveMedication}
              style={{
                background: purple,
                color: "#fff",
                borderRadius: "20px",
                padding: "4px 18px",
              }}
            >
              Save
            </button>

            {!isCreate && editingMedicationIndex !== null && (
              <button
                className="btn btn-sm"
                onClick={deleteCurrentMedication}
                style={{
                  background: "#c0392b",
                  color: "#fff",
                  borderRadius: "20px",
                }}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      )}

      {/* LIST */}
      {isOpen &&
        medication.map((item, idx) => (
          <div
            key={item.id ?? idx}
            className="d-flex justify-content-between align-items-center mb-2"
            style={{ fontSize: "14px", gap: "8px" }}
          >
            <div>
              <div>
                {item.medication_name} | {item.dosage_and_frequency}
              </div>
              <div style={{ fontSize: "12px", color: "#777" }}>
                {item.notes || "Medication details"}
              </div>
            </div>

            {!isCreate && (
              <FontAwesomeIcon
                icon={faPen}
                style={{ cursor: "pointer", color: purple, fontSize: "14px" }}
                onClick={() => {
                  setNewMedication({
                    id: item.id ?? null,
                    medication_id: item.medication_id ?? null,
                    medication_name: item.medication_name ?? "",
                    dosage_and_frequency: item.dosage_and_frequency ?? "",
                    notes: item.notes ?? "",
                  });

                  setEditingMedicationIndex(idx);
                  setShowMedicationForm(true);

                  
                  setIsCustomSelected(
                    item.medication_id == null && !!item.medication_name,
                  );
                }}
              />
            )}
          </div>
        ))}
    </div>
  );
};

export default MedicationSubsection;
